import React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

// much gentler than the home page cards (17.5deg) so the videos stay usable
const MAX_TILT = 2.5;

const ProjectSection = ({ id, title, description, tech, href, linkText = "View on GitHub", playlist, icon, videos = [] }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useTransform(useSpring(y), [-0.5, 0.5], [MAX_TILT, -MAX_TILT]);
  const rotateY = useTransform(useSpring(x), [-0.5, 0.5], [-MAX_TILT, MAX_TILT]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
  <motion.section
    id={id}
    onMouseMove={handleMouseMove}
    onMouseLeave={handleMouseLeave}
    style={{ rotateX, rotateY, transformPerspective: 1200 }}
    className="relative z-[1000] w-full max-w-4xl rounded-xl shadow-xl shadow-black/30 bg-gradient-to-br from-emerald-900/80 to-emerald-700/80 p-3"
  >
    <div className="rounded-xl bg-gradient-to-br from-emerald-200/75 to-emerald-100/75 p-6 shadow-lg">
      <div className="flex flex-col items-center gap-5 sm:flex-row">
        {icon && <img src={icon} alt={`${title} icon`} className="h-32 w-32 shrink-0 object-contain" />}
        <div className="text-center sm:text-left">
          <p className="text-3xl font-bold">{title}</p>
          <p className="mt-2 text-lg font-medium">{description}</p>
          <div className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-1 sm:justify-start">
            {href && (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-bold uppercase tracking-wide underline"
              >
                {tech} · {linkText} →
              </a>
            )}
            {playlist && (
              <a
                href={playlist}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-bold uppercase tracking-wide underline"
              >
                Playlist on YouTube →
              </a>
            )}
          </div>
        </div>
      </div>

      {videos.length > 0 && <div className="mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3">
        {videos.map((id, i) => (
          <iframe
            key={id}
            className="aspect-video w-[80%] shrink-0 snap-start rounded-lg md:w-[45%]"
            src={`https://www.youtube-nocookie.com/embed/${id}`}
            title={`${title} video ${i + 1}`}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ))}
      </div>}
    </div>
  </motion.section>
  );
};

export default ProjectSection;
